import { create } from 'zustand'

import { AddressEntity } from '@/entities/address.entity'
import { CompanyEntity } from '@/entities/company.entity'
import { registerBrowserSessionCleanup } from '@/lib/auth/browser-session-boundary'
import { CompanyStructure, OnboardingStatus } from '@/types/enums'

const _onboardingDefaultState = {
  coreInformation: { company_type: 'llc' },
  documents: {
    formation: 'pending',
    tax: 'pending',
  },
  addressForm: undefined,
  optionalInformation: undefined,
}

// Cache TTL: 5 minutes
const CACHE_TTL_MS = 5 * 60 * 1000

export interface CustomerActiveSubscription {
  plan: string
  currentPeriodEnd: string | null
}

export interface CustomerBootstrapCache {
  user: {
    id: number | null
    userId: string
    email: string | null
    firstname: string | null
    lastname: string | null
    role: string | null
    companyId: string | null
  } | null
  onboardingStatus: OnboardingStatus | string | null
  activeSubscriptions: CustomerActiveSubscription[]
  hasWireTransferFullSuite: boolean
  lastFetchedAt: number | null
}

/**
 * How the shell's customer bootstrap ended. `companyDetail` alone cannot tell
 * "this account has no company" (a 200 with `company: null`) from "the read
 * failed" (the shell drops its loading gate with the store untouched), so the
 * shell records which one happened here.
 */
export type CustomerBootstrapStatus = 'pending' | 'loaded' | 'failed'

export const isCacheStale = (lastFetchedAt: number | null): boolean => {
  if (!lastFetchedAt) return true
  return Date.now() - lastFetchedAt > CACHE_TTL_MS
}

export type GeneralStore = {
  companyDetail: CompanyEntity & { company_structure: CompanyStructure }
  companyAddressDetail: AddressEntity
  customerBootstrapCache: CustomerBootstrapCache
  customerBootstrapStatus: CustomerBootstrapStatus
  setCustomerBootstrapCache: (cache: Partial<CustomerBootstrapCache>) => void
  clearCaches: () => void
  // eslint-disable-next-line @typescript-eslint/no-explicit-any -- dynamic store requires index signature with any for setStoreState/setStoreObjectState
  [key: string]: any
}

const initialCustomerBootstrapCache: CustomerBootstrapCache = {
  user: null,
  onboardingStatus: null,
  activeSubscriptions: [],
  hasWireTransferFullSuite: false,
  lastFetchedAt: null,
}

export const useGeneralStore = create<GeneralStore>((set) => ({
  companyDetail: new CompanyEntity({}) as CompanyEntity & { company_structure: CompanyStructure },
  companyAddressDetail: new AddressEntity({}),
  organization: undefined,
  user: undefined,

  // Cache state
  customerBootstrapCache: initialCustomerBootstrapCache,
  customerBootstrapStatus: 'pending',

  // Cache setters
  setCustomerBootstrapCache: (cache) =>
    set((state) => ({
      customerBootstrapCache: { ...state.customerBootstrapCache, ...cache, lastFetchedAt: Date.now() },
    })),
  clearCaches: () =>
    set(() => ({
      customerBootstrapCache: initialCustomerBootstrapCache,
      customerBootstrapStatus: 'pending',
    })),

  setStoreState: (key: string, value: unknown) => set(() => ({ [key]: value })),
  setStoreObjectState: (key: string, value: unknown) =>
    set((state) => {
      const keys = key.split('.')
      const newState = { ...state }
      let current = newState
      for (let i = 0; i < keys.length - 1; i++) {
        current[keys[i]] = { ...current[keys[i]] }
        current = current[keys[i]]
      }
      current[keys[keys.length - 1]] = value
      return newState
    }),
}))

// The account's user, company, address and bootstrap cache. Sign-out keeps the
// tab (a client-side navigation to /sign-in) and a passkey sign-in reaches the
// dashboard the same way, so the next account must not inherit any of it.
registerBrowserSessionCleanup(() => {
  useGeneralStore.setState(useGeneralStore.getInitialState(), true)
})
